import { withStyles, Grid, Typography, Paper } from '@material-ui/core';
import React from 'react'; // eslint-disable-line
import { ExpansionPanel } from '../../../../../../forms/form-components/expansion-panel/expansion-panel';
import styles, { StyledComponent } from '../../../../../../styles/styles';
import { LabeledValue } from '../../../../../../controls/labeled-value';
import { FormDetails, getFormDetails } from '../../form-details-reducer';
import { connect } from 'react-redux';
import { State } from '../../../../../../redux/root-reducer';
import { getDisplayDate } from '../../../../../../shared-functions';

interface BaseProps {
    formDetails?: FormDetails;
    disableEditButton?: boolean;
}

interface Props extends BaseProps, StyledComponent {}

function toUpperCaseBankFirstLetterOnly(str: string) {
    if (!str) {
        return '';
    }
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

const paymentDetailsView: React.FunctionComponent<Props> = ({ classes, formDetails }: Props) => {
    const paymentInfo = formDetails?.payment?.paymentInformation || {};
    const isCessionary = paymentInfo.paymentBeneficiary === 'Cessionary';
    const isIndividual = paymentInfo.doesAccountBelongToAnIndividualOrABusiness === 'Individual';
    const isCompany = paymentInfo.doesAccountBelongToAnIndividualOrABusiness === 'Company';
    const isPremiumPayer = paymentInfo.paymentBeneficiary === 'Premium Payer';
    const isSaResident = paymentInfo.isTheAccountHolderASouthAfricanResident === 'Yes';
    const isNonResident = paymentInfo.isTheAccountHolderASouthAfricanResident === 'No';

    return (
        <ExpansionPanel name="Payment details" style={{ marginLeft: -24, marginRight: -24 }}>
            <React.Fragment>
                <Grid container justify="space-between" className={classes.marginTop2}>
                    <Grid container item xs={12} alignItems="center">
                        <Typography className={classes.heading} variant="body1">
                            Payment Details
                        </Typography>
                    </Grid>
                </Grid>
                <Paper className={classes.paper}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6} lg={3}>
                            <LabeledValue
                                label="Payment must be made into the account of"
                                value={paymentInfo.paymentBeneficiary}
                            />
                        </Grid>
                        {!isCessionary && (
                            <>
                                <Grid item xs={12} md={6} lg={3}>
                                    <LabeledValue
                                        label="Bank Name"
                                        value={toUpperCaseBankFirstLetterOnly(
                                            paymentInfo.bankName || ''
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6} lg={3}>
                                    <LabeledValue
                                        label="Account Number"
                                        value={paymentInfo.accountNumber}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6} lg={3}>
                                    <LabeledValue
                                        label="Account Type"
                                        value={paymentInfo.accountType}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6} lg={3}>
                                    <LabeledValue
                                        label="Branch name"
                                        value={paymentInfo.branchName}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6} lg={3}>
                                    <LabeledValue
                                        label="Branch Code"
                                        value={paymentInfo.branchCode}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6} lg={3}>
                                    <LabeledValue
                                        label="Does account belong to an individual or a company?"
                                        value={
                                            paymentInfo.doesAccountBelongToAnIndividualOrABusiness
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} md={6} lg={3}>
                                    <LabeledValue
                                        label="Account holder relationship"
                                        value={paymentInfo.accountHolderRelationship}
                                    />
                                </Grid>
                                {isIndividual && (
                                    <>
                                        <Grid item xs={12} md={6} lg={3}>
                                            <LabeledValue
                                                label="Is the account holder a South African resident?"
                                                value={
                                                    paymentInfo.isTheAccountHolderASouthAfricanResident
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6} lg={3}>
                                            <LabeledValue
                                                label="Full first names of account holder"
                                                value={paymentInfo.fullFirstNameOfAccountHolder}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6} lg={3}>
                                            <LabeledValue
                                                label="Surname"
                                                value={paymentInfo.surname}
                                            />
                                        </Grid>
                                        {isSaResident && (
                                            <Grid item xs={12} md={6} lg={3}>
                                                <LabeledValue
                                                    label="ID number"
                                                    value={paymentInfo.idNumber}
                                                />
                                            </Grid>
                                        )}
                                        {isNonResident && (
                                            <>
                                                <Grid item xs={12} md={6} lg={3}>
                                                    <LabeledValue
                                                        label="Passport number"
                                                        value={paymentInfo.passportNumber}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6} lg={3}>
                                                    <LabeledValue
                                                        label="Country of residence"
                                                        value={paymentInfo.countryOfResidence}
                                                    />
                                                </Grid>
                                            </>
                                        )}
                                        {isPremiumPayer && (
                                            <>
                                                <Grid item xs={12} md={6} lg={3}>
                                                    <LabeledValue
                                                        label="Relationship to Policyholder"
                                                        value={
                                                            paymentInfo.relationshipToPolicyHolder
                                                        }
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6} lg={3}>
                                                    <LabeledValue
                                                        label="Is the monthly premium being paid using this account?"
                                                        value={
                                                            paymentInfo.isTheMonthlyPremiumBeingPaidUsingThisAccount
                                                        }
                                                    />
                                                </Grid>
                                            </>
                                        )}
                                    </>
                                )}
                                {isCompany && (
                                    <>
                                        <Grid item xs={12} md={6} lg={3}>
                                            <LabeledValue
                                                label="Country of company"
                                                value={paymentInfo.countryOfcompany}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6} lg={3}>
                                            <LabeledValue
                                                label="Company name"
                                                value={paymentInfo.companyName}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6} lg={3}>
                                            <LabeledValue
                                                label="Company registration date"
                                                value={getDisplayDate(
                                                    paymentInfo.companyRegistrationDate
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6} lg={3}>
                                            <LabeledValue
                                                label="Company registration number"
                                                value={paymentInfo.companyRegistrationNumber}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6} lg={3}>
                                            <LabeledValue
                                                label="Relationship to Policyholder"
                                                value={paymentInfo.relationshipToPolicyHolder}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6} lg={3}>
                                            <LabeledValue
                                                label="Is the monthly premium being paid using this account?"
                                                value={
                                                    paymentInfo.isTheMonthlyPremiumBeingPaidUsingThisAccount
                                                }
                                            />
                                        </Grid>
                                    </>
                                )}
                            </>
                        )}
                    </Grid>
                </Paper>
            </React.Fragment>
        </ExpansionPanel>
    );
};

const mapStateToProps = (state: State, ownProps: BaseProps) => ({
    formDetails: ownProps.formDetails || getFormDetails(state),
    disableEditButton: ownProps.disableEditButton || false,
});

export const PaymentDetailsView = connect(mapStateToProps)(withStyles(styles)(paymentDetailsView));
